import {ICustomerDetail, ISearchCustomer} from './types';
import {CustomerDetailType} from '../CustomerTab/types';
import React, { useEffect, useMemo, useState } from 'react';
import IconsService from 'external-services/icons-service';
import {IconTypes} from 'external-services/icons-service/icons.types';
import style from './style.module.css';
import classNames from 'classnames';
import typ from 'design-v2/globals/typography.module.css';
import CustomerDetailInput from 'design-v2/common/CustomerTab/CustomerDetailInput';
import color from 'design-v2/globals/colors/colors.module.css';
import Box from '@material-ui/core/Box';
import FlagPhonePickerValidationHelper
  from 'external-components/flag-phone-picker-react/FlagPhonePickerValidationHelper';
import PhoneNumberService from 'external-services/phone-number-service';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import {CustomerMode} from 'shared-types';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';

const EmailIcon = IconsService.getIcon(IconTypes.mailOutlineIcon);
const PersonSearch = IconsService.getIcon(IconTypes.personSearch);
const PHONE_ERROR_MESSAGE = 'Phone: 6 or more digits';

const validator: any = {
  phone: (value: keyof ICustomerDetail) => {
    const number = PhoneNumberService.parseNumber(value, true)?.nationalNumber;
    if (value === null || number?.length >= 6) {
      return undefined;
    }
    return PHONE_ERROR_MESSAGE;
  },
};
const WIDE_LAYOUT = `'phone first' 'email last' 'company last'`;
const NARROW_LAYOUT = `'phone' 'first' 'last' 'email' 'company'`;

const CLASS_PRE = 'ExistingCustomerSearch'

const DiaryTheme = {
  root: {
    [`& .${CLASS_PRE}-MuiInput-underline:before`]: {
      borderBottomColor: '#DFDFDF',
    },
  }
}
const SearchCustomer = (props: ISearchCustomer) => {
  const {
    customer,
    handleChange,
    inputOnFocus,
    country,
    errors,
    setErrors,
    isMobile,
    isWideVersion,
    view,
    setView,
    showPhoneErrorMsg = false,
    globalCustomer,
    duplicateCustomer,
    layout = 'narrow',
  } = props;

  useEffect(() => {
    !!customer &&
      Object.keys(customer).forEach((key) => {
        const value = customer[key as keyof ICustomerDetail];
        if (validator[key]) {
          if (!value) {
            setErrors && setErrors(key as any, undefined);
          } else {
            const error = validator[key](value);
            setErrors && setErrors(key as any, error);
          }
        }
      });
  }, [customer]);

  const nationalNumber = useMemo(() => {
    return customer?.phone
      ? PhoneNumberService.parseNumber(customer.phone, true)?.nationalNumber
      : undefined;
  }, [customer]);

  const shouldSkipAutoFocusRef = React.useRef(false);
  const handleFocus = (type: CustomerDetailType) => {
    shouldSkipAutoFocusRef.current = true;
    inputOnFocus(type);
  };
  const [showPhoneInput, setShowPhoneInput] = useState(true);
  /**
   * as NBI-8604 change in flag-phone-picker-react to improve the old iPad performance, the phone number issue happen,
   * so have to add the following useEffect to show phone input only when view is search
   */
  useEffect(() => {
    if (view !== CustomerMode.SEARCH) {
      setTimeout(() => {
        setShowPhoneInput(false);
      }, 500);
    } else {
      setShowPhoneInput(true);
    }
  }, [view])
  return (
    <div
      className={classNames({
        [style.customerTabContainer]: !isMobile,
      })}
    >
      <div
        className={classNames({
          [style.labelContainer]: true,
          [style.searchCustomerHeader]: !isWideVersion && !isMobile,
        })}
      >
        <span
          className={classNames({
            [style.label]: true,
            [typ.typBold]: true,
          })}
        >
          Customer Search
        </span>
        {isMobile && (
          <div
            className={classNames({
              [style.clickableIconContainer]: true,
            })}
          >
            <div
              className={classNames({
                [style.clickableIcon]: view === CustomerMode.SEARCH,
              })}
            >
              <PersonSearch />
            </div>
            <div
              className={classNames({
                [style.clickableIcon]: view === CustomerMode.NEW,
                [style.unClickIcon]: true,
              })}
            >
              <PersonAddIcon onClick={() => setView(CustomerMode.NEW)} />
            </div>
          </div>
        )}
      </div>
      {isMobile && view === CustomerMode.SEARCH && duplicateCustomer && (
        <div
          className={classNames({
            [style.error]: true,
            [style.duplicatedErrorMsg]: true,
          })}
        >
          <p>{`Customer ${
            (globalCustomer?.firstName ? globalCustomer?.firstName + ' ' : '') +
            (globalCustomer?.lastName ? globalCustomer?.lastName + ' ' : '')
          }`}</p>
          <p>
            {(globalCustomer?.phone ? globalCustomer?.phone + ' ' : '') +
              (globalCustomer?.email ? globalCustomer?.email + ' ' : '')}
          </p>
          <p>already exists. Please select or edit the bellow customer.</p>
        </div>
      )}

      <span
        className={classNames({
          [style.helperText]: true,
        })}
      >
          First enter the customer's details.
        </span>

      <div
        className={classNames({
          [style.detailsContainer]: true,
        })}
      >
        <div
          className={style.fieldContainer}
          style={{
            display: 'grid',
            gridColumnGap: '15px',
            gridTemplateAreas: layout === 'wide' ? WIDE_LAYOUT : NARROW_LAYOUT,
            gridTemplateColumns: layout === 'wide' ? '1fr 1fr' : '1fr',
          }}
        >
          <div className={classNames({
            [style.phonePicker]: true,
            [style.phonePickerIsWide]: true,
          })} style={{ gridArea: 'phone' }}>
            {showPhoneInput && <FlagPhonePickerValidationHelper
              isNewCustomerForm={true}
              autoFocusOnLoad={!isWideVersion}
              skipAutoComplete={true}
              customerCountry={country}
              customerPhone={nationalNumber || ''}
              classPrefix={!isMobile ? CLASS_PRE : null}
              handlePhoneChange={(e: string, phoneWithoutPrefix: string) => {
                handleChange(CustomerDetailType.phone, e, phoneWithoutPrefix);
                inputOnFocus(CustomerDetailType.phone);
              }}
              usePlaceholderForLabel={true}
              shouldSkipAutoFocus = {() => shouldSkipAutoFocusRef.current}
            />}
          </div>

          <div
            style={{ gridArea: 'first' }}
            className={classNames({
              [style.firstNameContainer]: layout === 'wide',
            })}
          >
            <CustomerDetailInput
              inputOnFocus={() => handleFocus(CustomerDetailType.firstName)}
              muiIcon={<PersonOutlineIcon className={color.clrGreyMedium} />}
              placeholder={'First Name'}
              detail={customer?.firstName || ''}
              handleChange={(value: string) => handleChange(CustomerDetailType.firstName, value)}
              themeProps={DiaryTheme}
            />
          </div>

          <div style={{ gridArea: 'last' }}>
            <CustomerDetailInput
              inputOnFocus={() => handleFocus(CustomerDetailType.lastName)}
              muiIcon={<PersonOutlineIcon className={color.clrGreyMedium} />}
              placeholder={'Surname'}
              detail={customer?.lastName || ''}
              handleChange={(value: string) => handleChange(CustomerDetailType.lastName, value)}
              themeProps={DiaryTheme}
            />
          </div>

          <div style={{ gridArea: 'email' }}>
            <CustomerDetailInput
              inputOnFocus={() => handleFocus(CustomerDetailType.email)}
              muiIcon={<EmailIcon className={color.clrGreyMedium} />}
              placeholder={'Email'}
              detail={customer?.email || ''}
              handleChange={(value: string) => handleChange(CustomerDetailType.email, value)}
              themeProps={DiaryTheme}
            />
          </div>

          <div style={{ gridArea: 'company' }}>
            <CustomerDetailInput
              inputOnFocus={() => handleFocus(CustomerDetailType.company)}
              muiIcon={<BusinessOutlinedIcon className={color.clrGreyMedium} />}
              placeholder={'Company'}
              detail={customer?.company || ''}
              handleChange={(value: string) => handleChange(CustomerDetailType.company, value)}
              themeProps={DiaryTheme}
            />
          </div>

        </div>
        <Box display="flex" flexDirection="column">
          {showPhoneErrorMsg && (
            <span
              className={classNames({
                [style.error]: true,
                [style.phoneError]: true,
              })}
            >
              {PHONE_ERROR_MESSAGE}
            </span>
          )}
          {errors?.phone ? <span className={style.error}>{errors.phone}</span> : null}
          {errors?.email ? <span className={style.error}>{errors.email}</span> : null}
        </Box>
      </div>
    </div>
  );
};
export default SearchCustomer;
